package com.evoapps.evoshift.domain.usecase.user

import com.evoapps.evoshift.domain.market.payroll.PayrollProfile
import com.evoapps.evoshift.domain.model.CompensationModel
import com.evoapps.evoshift.domain.model.Competence
import com.evoapps.evoshift.domain.model.Engagement
import com.evoapps.evoshift.domain.model.FinancialEngagement
import com.evoapps.evoshift.domain.model.SensitiveUserData
import com.evoapps.evoshift.domain.model.User
import com.evoapps.evoshift.domain.model.UserPermission
import com.evoapps.evoshift.domain.repository.UserRepository
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime
import kotlin.random.Random
import com.evoapps.evoshift.domain.util.AppClock.now

class CreateEmployeeUseCase(
    private val userRepository: UserRepository
) {
    suspend operator fun invoke(
        // Dane organizacyjne
        workplaceId: String,
        workplaceName: String,
        displayName: String,
        role: String, // np. "Barista"
        permission: UserPermission,
        skills: List<String>,
        isPrimary: Boolean,
        competence: Competence = Competence.INDEPENDENT,
        startDate: String? = null,
        endDate: String? = null,

        // Dane finansowe i kontraktowe
        contractType: String, // "UoP", "B2B"
        hourlyRate: Double? = null,
        monthlySalary: Double? = null,
        employerCost: Double = 0.0, // unused: the market's payroll rules compute it
        fte: Double = 1.0, // Wymiar etatu (domyślnie 1.0)
        declaredNumberOfHours: Int? = null,
        currency: String = "PLN",
        payrollProfile: PayrollProfile = PayrollProfile(),

        // Dane osobowe (Sensitive - General)
        email: String? = null,
        phoneNumber: String? = null,
        address: String? = null,
        bankAccountNumber: String? = null,
        /**
         * Pay and personal data are written only by those who may (Capability.MANAGE_PAY): an
         * operational manager adds the person without them, a financial manager fills them in.
         */
        includeSensitive: Boolean = true,
        /** SH-117: the number in the payroll software (RCP card) for the accountant's exports. */
        payrollId: String? = null
    ): Result<String> {
        return try {
            // A "shadow" profile: the person has no account yet. Its id has the shape of a Firestore
            // auto-id; acceptInvite (Cloud Function) moves it onto the real uid when they join.
            val newUserId = generateRandomId()

            // 2. Obsługa dat
            val now = now()
            val nowStr = now.toLocalDateTime(TimeZone.currentSystemDefault()).date.toString()
            val finalStartDate = startDate ?: nowStr

            // 3-4. Pay terms (to sensitive/{workplaceId})
            val financialEngagement = newFinancialEngagement(
                contractType = contractType,
                hourlyRate = hourlyRate,
                monthlySalary = monthlySalary,
                fte = fte,
                declaredNumberOfHours = declaredNumberOfHours,
                currency = currency,
                payrollProfile = payrollProfile,
                payrollId = payrollId,
                employerCost = employerCost
            )

            // 5. Tworzenie Zaangażowania Publicznego (do users/{uid})
            val engagement = Engagement(
                workplaceId = workplaceId,
                // cachedWorkplaceName = workplaceName, // Jeśli masz to w modelu Engagement
                role = role,
                // skills = skills, // Jeśli masz to w modelu Engagement
                permission = permission,
                isActive = true,
                startDate = finalStartDate,
                isPrimary = isPrimary,
                cachedWorkplaceName = workplaceName,
                skills = skills,
                endDate = endDate,
                competence = competence
            )

            // 6. Tworzenie Użytkownika (Shadow User)
            val newUser = User(
                uid = newUserId,
                displayName = displayName,
                photoUrl = null,
                engagements = mapOf(workplaceId to engagement),
                isShadow = true, // WAŻNE: To jest konto stworzone przez managera
                createdAt = now.toString()
            )

            // 7. Zbieranie danych wrażliwych (General + Workplace)
            val sensitiveData = SensitiveUserData(
                userId = newUserId,
                email = email,
                phoneNumber = phoneNumber,
                address = address,
                bankAccountNumber = bankAccountNumber,
                financialEngagements = mapOf(workplaceId to financialEngagement)
            )

            // 8. Zapis do bazy
            userRepository.createEmployee(newUser, sensitiveData.takeIf { includeSensitive }).getOrThrow()

            Result.success(newUserId)
        } catch (e: Exception) {
            Result.failure(e)
        }
    }
}

/**
 * The pay terms of a person new to a workplace, as the employee form gives them: a monthly salary
 * when one is set, else an hourly rate. Shared by a new person (CreateEmployeeUseCase) and an
 * existing one taken into another workplace (SH-192, AddEngagementUseCase).
 */
fun newFinancialEngagement(
    contractType: String,
    hourlyRate: Double?,
    monthlySalary: Double?,
    fte: Double = 1.0,
    declaredNumberOfHours: Int? = null,
    currency: String = "PLN",
    payrollProfile: PayrollProfile = PayrollProfile(),
    payrollId: String? = null,
    employerCost: Double = 0.0
): FinancialEngagement {
    val compensation = if (monthlySalary != null && monthlySalary > 0) {
        CompensationModel.MonthlyFixed(
            baseSalary = monthlySalary,
            fte = fte,
            currency = currency,
            employerCost = employerCost
        )
    } else {
        CompensationModel.Hourly(
            rate = hourlyRate ?: 0.0,
            currency = currency,
            employerCost = employerCost
        )
    }
    return FinancialEngagement(
        contractTypeId = contractType,
        declaredNumberOfHours = declaredNumberOfHours,
        compensation = compensation,
        payrollProfile = payrollProfile,
        payrollId = payrollId?.trim()?.takeIf { it.isNotEmpty() }
    )
}

fun generateRandomId(): String {

    val chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789"

    return (1..20)

        .map { chars[Random.nextInt(chars.length)] }

        .joinToString("")

}